import { derived, get, writable, type Readable } from 'svelte/store';
import {
  serializableToEngineConfig,
  toSerializableAIConfig,
  type Difficulty,
  type SavedMatch,
} from '@texas42/app-model';
import type {
  FilteredGameState,
  GameAction,
  GameClient,
  GameConfig,
  GameView,
  LocalGame,
  PlayerInfo,
  Room,
} from '@texas42/game-runtime';
import {
  actionToId,
  createLocalGame,
  createSetupState,
  GAME_PHASES,
  getPlayerSeatName,
  resolveActionIds,
} from '@texas42/game-runtime';
import { toEngineGameConfig } from '../platform/persistence/config-mapper.js';
import { restoreSavedMatch } from '../platform/persistence/restore.js';
import { BID_REVEAL_DURATION_MS } from '../features/game/bid-reveal.js';
import { deriveMatchAiRngSeed } from '../workers/ai-decision-engine.js';
import type { SessionMode } from '../app/types.js';
import { buildGameSessionView } from './build-game-session-view.js';
import type {
  GameSessionController,
  GameSessionControllerDeps,
  GameSessionControllerOptions,
  GameSessionView,
  ProductGameConfig,
} from './types.js';
import { PLAY_ADVICE_UNAVAILABLE } from '../workers/play-advice.js';

const DEFAULT_SESSION_ID = 'player-0';

function detectTestMode(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('testMode') === 'true';
}

function defaultPlayerTypes(testMode: boolean): ('human' | 'ai')[] {
  return testMode
    ? ['human', 'human', 'human', 'human']
    : ['human', 'ai', 'ai', 'ai'];
}

function createPendingView(playerTypes: ('human' | 'ai')[]): GameView {
  const placeholderState = createSetupState({ playerTypes });

  const players: PlayerInfo[] = playerTypes.map((type, index) => ({
    playerId: index,
    controlType: type,
    sessionId: `${type === 'human' ? 'player' : 'ai'}-${index}`,
    connected: true,
    name: getPlayerSeatName(index),
    capabilities: [],
  }));

  const filteredState: FilteredGameState = {
    ...placeholderState,
    players: placeholderState.players.map((player) => ({
      ...player,
      hand: [...player.hand],
      handCount: player.hand.length,
    })),
  };

  return {
    state: filteredState,
    validActions: [],
    transitions: [],
    players,
    metadata: {
      gameId: 'initializing',
    },
    derived: {
      currentTrickWinner: -1,
      isTrickComplete: false,
      handDominoMeta: [],
      currentHandPoints: [0, 0] as [number, number],
      handOutcome: null,
    },
  };
}

function productConfigToEngineConfig(
  input: ProductGameConfig,
  seed: number,
): GameConfig {
  if (input.serializable) {
    return serializableToEngineConfig({
      ...input.serializable,
      shuffleSeed: seed,
    });
  }

  const playerTypes = input.playerTypes ?? defaultPlayerTypes(input.testMode ?? detectTestMode());
  const layers =
    input.layers ?? (input.matchFormat === 'practice-hand' ? ['oneHand', 'consensus'] : ['consensus']);

  return {
    playerTypes,
    shuffleSeed: seed,
    ...(input.dealer !== undefined ? { dealer: input.dealer } : {}),
    ...(input.theme ? { theme: input.theme } : {}),
    ...(input.colorOverrides && Object.keys(input.colorOverrides).length > 0
      ? { colorOverrides: input.colorOverrides }
      : {}),
    ...(input.houseRules ? { houseRules: input.houseRules } : {}),
    layers,
  };
}

export function createGameSessionController(
  deps: GameSessionControllerDeps,
  options: GameSessionControllerOptions = {},
): GameSessionController {
  const mode: SessionMode = options.mode ?? 'live';
  const aiEnabled = options.aiEnabled ?? mode === 'live';
  const testMode = detectTestMode();
  const playerTypes = defaultPlayerTypes(testMode);

  let localGame: LocalGame | undefined;
  let unsubscribe: (() => void) | undefined;
  let savedMatch: SavedMatch | null = null;
  let activeDifficulty: Difficulty = 'competitive';
  let stateRevision = 0;
  let paused = false;
  let bidRevealHold = false;
  let bidRevealTimer: ReturnType<typeof setTimeout> | undefined;
  let previousEnginePhase: string | null = null;
  let disposed = false;
  let findingSeed = false;

  const clientView = writable<GameView>(createPendingView(playerTypes));
  const currentSessionIdStore = writable<string>(DEFAULT_SESSION_ID);

  interface SessionMeta {
    matchId: string | null;
    mode: SessionMode;
    paused: boolean;
    bidRevealActive: boolean;
    aiEnabled: boolean;
    stateRevision: number;
    savedMatch: SavedMatch | null;
    findingSeed: boolean;
  }

  const metaStore = writable<SessionMeta>({
    matchId: null as string | null,
    mode,
    paused,
    bidRevealActive: false,
    aiEnabled,
    stateRevision,
    savedMatch,
    findingSeed,
  });

  function bumpRevision(): number {
    stateRevision += 1;
    metaStore.update((value) => ({ ...value, stateRevision }));
    return stateRevision;
  }

  function buildSessionView(
    view: GameView,
    sessionId: string,
    meta: {
      matchId: string | null;
      mode: SessionMode;
      paused: boolean;
      bidRevealActive: boolean;
      aiEnabled: boolean;
      stateRevision: number;
      savedMatch: SavedMatch | null;
      findingSeed: boolean;
    },
  ): GameSessionView {
    return buildGameSessionView({
      view,
      sessionId,
      mode: meta.mode,
      matchId: meta.matchId,
      stateRevision: meta.stateRevision,
      paused: meta.paused,
      bidRevealActive: meta.bidRevealActive,
      aiEnabled: meta.aiEnabled,
      savedMatch: meta.savedMatch,
      findingSeed: meta.findingSeed,
      testMode,
    });
  }

  const state: Readable<GameSessionView> = derived(
    [clientView, currentSessionIdStore, metaStore],
    ([$view, $sessionId, $meta]) => buildSessionView($view, $sessionId, $meta),
  );

  function updateMeta(patch: Partial<SessionMeta>): void {
    metaStore.update((value) => ({ ...value, ...patch }));
  }

  function clearBidRevealTimer(): void {
    if (bidRevealTimer !== undefined) {
      clearTimeout(bidRevealTimer);
      bidRevealTimer = undefined;
    }
  }

  function startBidRevealHold(): void {
    clearBidRevealTimer();
    bidRevealHold = true;
    updateMeta({ bidRevealActive: true });
    bidRevealTimer = setTimeout(() => {
      bidRevealTimer = undefined;
      bidRevealHold = false;
      updateMeta({ bidRevealActive: false });
      deps.aiClient.nudge();
    }, BID_REVEAL_DURATION_MS);
  }

  function syncBidRevealForPhase(phase: string): void {
    if (previousEnginePhase === GAME_PHASES.TRUMP_SELECTION && phase === GAME_PHASES.PLAYING) {
      startBidRevealHold();
    }
    previousEnginePhase = phase;
  }

  async function persistCurrentState(view: GameView): Promise<void> {
    if (!savedMatch || paused || disposed || mode === 'replay') return;
    if (view.metadata.gameId === 'initializing') return;

    const actionIds = view.state.actionHistory.map(actionToId);
    const currentMarks: [number, number] = [
      view.state.teamMarks[0],
      view.state.teamMarks[1],
    ];

    let status = savedMatch.status;
    if (
      view.state.phase === GAME_PHASES.GAME_END ||
      view.state.phase === GAME_PHASES.ONE_HAND_COMPLETE
    ) {
      status = 'completed';
    }

    const handFinished =
      view.state.phase === GAME_PHASES.SCORING ||
      view.state.phase === GAME_PHASES.GAME_END ||
      view.state.phase === GAME_PHASES.ONE_HAND_COMPLETE;
    const handAiConfig = deps.aiClient.getHandAiConfig();
    const handDiagnostics = handFinished ? deps.aiClient.drainHandDiagnostics() : [];
    const nextSavedMatch: SavedMatch = {
      ...savedMatch,
      actionIds,
      currentMarks,
      status,
      ...(status === 'completed'
        ? { completedAt: new Date(deps.clock.now()).toISOString() }
        : {}),
      ...(!handFinished && handAiConfig ? { activeHandAiConfig: handAiConfig } : {}),
      ...(handFinished && handDiagnostics.length > 0
        ? { lastHandAiDiagnostics: handDiagnostics }
        : {}),
      ...(handFinished && handAiConfig ? { lastHandAiConfig: handAiConfig } : {}),
    };
    if (handFinished) delete nextSavedMatch.activeHandAiConfig;
    savedMatch = nextSavedMatch;
    updateMeta({ savedMatch });

    try {
      await deps.matchPersistence.persistMatchUpdate(savedMatch);
    } catch (error) {
      deps.logger.error('Failed to persist match update', {
        matchId: savedMatch.id,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  function installClient(client: GameClient): void {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = undefined;
    }

    unsubscribe = client.subscribe((view: GameView) => {
      // Hold before AI clients are notified (human seat is first in Room broadcast order).
      syncBidRevealForPhase(view.state.phase);
      bumpRevision();
      clientView.set(view);
      void persistCurrentState(view);
    });

    if (client.view) {
      previousEnginePhase = client.view.state.phase;
      bumpRevision();
      clientView.set(client.view);
      void persistCurrentState(client.view);
    }
  }

  function getPlayerIndexForAction(action: GameAction): number {
    if ('player' in action && typeof action.player === 'number') {
      return action.player;
    }

    if (
      action.type === 'complete-trick' ||
      action.type === 'score-hand' ||
      action.type === 'redeal'
    ) {
      return 0;
    }

    return 0;
  }

  function replayActionsInRoom(room: Room, actionIds: string[], config: GameConfig): void {
    try {
      const seed = config.shuffleSeed;
      if (!seed) {
        deps.logger.error('Cannot replay actions: no shuffle seed in config');
        return;
      }

      const actions = resolveActionIds(actionIds, config, seed);
      for (let index = 0; index < actions.length; index += 1) {
        const action = actions[index];
        if (!action) {
          deps.logger.error('Action is undefined at index', { index });
          break;
        }

        const playerIndex = getPlayerIndexForAction(action);
        const playerId = `player-${playerIndex}`;
        const result = room.executeRecordedAction(playerId, action);
        if (!result.success) {
          deps.logger.error('Action replay failed', { error: result.error });
          break;
        }
      }
    } catch (error) {
      deps.logger.error('Failed to replay actions', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  function wireUpGame(
    config: GameConfig,
    actionIds?: string[],
    replayOptions?: { attachAiAfterReplay?: boolean },
  ): void {
    if (disposed) return;

    if (unsubscribe) {
      unsubscribe();
      unsubscribe = undefined;
    }
    if (localGame) {
      deps.aiClient.dispose();
      localGame.client.disconnect();
    }

    const configPlayerTypes = config.playerTypes ?? playerTypes;
    const aiPlayerIndexes = configPlayerTypes
      .map((type, index) => (type === 'ai' ? index : -1))
      .filter((index) => index >= 0);

    const hasActionsToReplay = actionIds !== undefined && actionIds.length > 0;
    localGame = createLocalGame(config, {
      aiPlayerIndexes,
      skipAIBehavior: true,
    });

    if (hasActionsToReplay) {
      replayActionsInRoom(localGame.room, actionIds, config);
    }

    if (aiEnabled && (!hasActionsToReplay || replayOptions?.attachAiAfterReplay !== false)) {
      const shuffleSeed = savedMatch?.config.shuffleSeed ?? config.shuffleSeed ?? 0;
      const wireOptions = {
        aiPlayerIndexes,
        getStateRevision: () => stateRevision,
        paused: () => paused || bidRevealHold,
        aiConfig: toSerializableAIConfig(activeDifficulty, deriveMatchAiRngSeed(shuffleSeed)),
        ...(savedMatch?.activeHandAiConfig
          ? { restoredHandAiConfig: savedMatch.activeHandAiConfig }
          : {}),
        ...(activeDifficulty === 'grandad'
          ? { getAdaptiveProfile: async () => deps.adaptiveProfile.load() }
          : {}),
      };
      deps.aiClient.wire(localGame, wireOptions);
    }

    installClient(localGame.client);
    localGame.client.send({ type: 'JOIN', playerIndex: 0, name: getPlayerSeatName(0) });
  }

  function executeActionImpl(action: GameAction): void {
    if (!localGame) throw new Error('Game not initialized');
    if (bidRevealHold && action.type === 'play') {
      throw new Error('Play is held until the bid reveal finishes');
    }

    const view = get(clientView);
    const sessionId = get(currentSessionIdStore);
    const currentPlayer = view.players.find(
      (player) => (player.sessionId ?? `player-${player.playerId}`) === sessionId,
    );

    if (
      !currentPlayer ||
      !currentPlayer.capabilities?.some((cap) => cap.type === 'act-as-player')
    ) {
      throw new Error('Current perspective cannot execute actions');
    }

    localGame.client.send({ type: 'EXECUTE_ACTION', action });
  }

  return {
    state,

    async create(input: ProductGameConfig): Promise<string> {
      const matchId = deps.idGenerator.nextId();
      const seed = input.shuffleSeed ?? Math.floor(Math.random() * 1_000_000);
      activeDifficulty = input.difficulty;
      const engineConfig = productConfigToEngineConfig(input, seed);

      savedMatch = await deps.matchPersistence.createMatch({
        id: matchId,
        name: input.name,
        config: input.serializable ?? {
          rulesetId: input.rulesetId,
          matchFormat: input.matchFormat,
          shuffleSeed: seed,
          ...(input.dealer !== undefined ? { dealer: input.dealer } : {}),
          ...(input.layers ? { layers: input.layers } : {}),
          ...(input.houseRules ? { houseRules: input.houseRules } : {}),
        },
        difficulty: input.difficulty,
        shuffleSeed: seed,
      });

      updateMeta({ matchId, savedMatch });
      wireUpGame(engineConfig, input.replayActionIds);
      return matchId;
    },

    async resume(matchId: string): Promise<void> {
      const match = await deps.matchRepository.get(matchId);
      if (!match) {
        throw new Error(`Match not found: ${matchId}`);
      }

      const restored = restoreSavedMatch(match);
      if (!restored.verification.verified) {
        deps.logger.warn('Restore verification reported issues', {
          errors: restored.verification.errors,
        });
      }

      savedMatch = match;
      activeDifficulty = match.difficulty;
      updateMeta({ matchId, savedMatch });

      const engineConfig = toEngineGameConfig(match.config);
      wireUpGame(engineConfig, match.actionIds, { attachAiAfterReplay: mode === 'live' });
    },

    executeTransition(transitionId: string): void {
      if (!localGame) throw new Error('Game not initialized');

      const view = get(clientView);
      const transition = view.transitions.find((entry) => entry.id === transitionId);
      if (!transition) {
        throw new Error(`Unknown transition: ${transitionId}`);
      }

      executeActionImpl(transition.action);
    },

    pause(): void {
      paused = true;
      deps.aiClient.pause();
      updateMeta({ paused: true });
    },

    resumePlay(): void {
      paused = false;
      deps.aiClient.resume();
      updateMeta({ paused: false });
    },

    async updateMatchPresentation(patch: {
      assistance?: import('@texas42/app-model').AssistanceLevel;
      confirmBeforePlay?: boolean;
    }): Promise<void> {
      if (!savedMatch || disposed || mode === 'replay') return;
      if (patch.assistance === undefined && patch.confirmBeforePlay === undefined) return;

      savedMatch = {
        ...savedMatch,
        config: {
          ...savedMatch.config,
          ...(patch.assistance !== undefined ? { assistance: patch.assistance } : {}),
          ...(patch.confirmBeforePlay !== undefined
            ? { confirmBeforePlay: patch.confirmBeforePlay }
            : {}),
        },
      };
      updateMeta({ savedMatch });
      await deps.matchPersistence.persistMatchUpdate(savedMatch);
    },

    requestPlayAdvice(): Promise<import('../workers/play-advice.js').PlayAdvice> {
      const view = get(clientView);
      const player = get(currentSessionIdStore).match(/player-(\d+)/)?.[1];
      const playerId = player === undefined ? 0 : Number.parseInt(player, 10);
      if (
        savedMatch?.config.assistance !== 'suggested-move' ||
        view.state.phase !== GAME_PHASES.PLAYING ||
        view.state.currentPlayer !== playerId ||
        !deps.aiClient.requestPlayAdvice
      ) {
        return Promise.resolve(PLAY_ADVICE_UNAVAILABLE);
      }

      return deps.aiClient.requestPlayAdvice({
        playerId,
        stateRevision,
        viewState: view.state,
        validActions: view.validActions,
      });
    },

    cancelPlayAdvice(): void {
      deps.aiClient.cancelPlayAdvice?.();
    },

    async saveNow(): Promise<void> {
      if (savedMatch) {
        await deps.matchPersistence.persistMatchUpdate(savedMatch);
      }
      await deps.matchPersistence.flush();
    },

    dispose(): void {
      if (disposed) return;
      disposed = true;
      paused = true;
      clearBidRevealTimer();
      bidRevealHold = false;
      deps.aiClient.dispose();

      if (unsubscribe) {
        unsubscribe();
        unsubscribe = undefined;
      }
      if (localGame) {
        localGame.client.disconnect();
        localGame = undefined;
      }

      updateMeta({ paused: true });
    },

    executeAction(action: GameAction): void {
      executeActionImpl(action);
    },

  };
}
